import { useContext } from 'react'
import { useIntl } from 'react-intl'
import { EmptyArray } from 'lib/array/arrayUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import useCruiseSearchFlashOffers from 'hooks/Cruise/useCruiseSearchFlashOffers'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'

interface Props {
  onClear: () => void
  onApply: () => void
  initialFilters?: App.OfferListFilters
  shouldIgnoreFlashOffers?: boolean
}

const filters = {}

function CruiseFilterFooter({
  onClear,
  onApply,
  initialFilters,
  shouldIgnoreFlashOffers,
}: Props) {
  const intl = useIntl()
  const globalFilters = useContext(GlobalSearchStateContext)
  const {
    cruiseLines,
    searchItems,
    secondarySearchItems,
    durationMax,
    durationMin,
    durationRange,
    flexibleMonths,
    checkinDate,
    checkoutDate,
  } = globalFilters

  const [searchCount, fetchingSearchCount] = useCruiseSearchCount({
    cruiseLines,
    destinationIds: searchItems.map(({ value }) => value),
    departureIds: secondarySearchItems.map(({ value }) => value),
    durationMin,
    durationMax,
    durationRange,
    departureMonths: flexibleMonths?.split(',') || EmptyArray,
    ...(!!checkinDate && !!checkoutDate && {
      departureStartDate: checkinDate.format(ISO_DATE_FORMAT),
      departureEndDate: checkoutDate.format(ISO_DATE_FORMAT),
    }),
    ...(initialFilters || {}),
  })

  const flashOffers = useCruiseSearchFlashOffers({
    filters,
    globalFilters,
  })

  const count = shouldIgnoreFlashOffers ? searchCount : searchCount + (flashOffers.offerCount || 0)

  return <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
    <BodyText data-testid="cruise-filter-footer-available-results" variant="medium">
      {!fetchingSearchCount && intl.formatMessage({
        id: '-gEOpq_QWkWdLgsbe80E',
        defaultMessage: '{count, plural, one {# cruise available} other {# cruises available}}',
        description: 'Number of available cruises in filter footer in the cruise section',
      }, { count })}
      {fetchingSearchCount && <LoadingAnimation size={8} />}
    </BodyText>

    <Group direction="horizontal" horizontalAlign="end" gap={16}>
      <TextButton
        kind="tertiary"
        onClick={onClear}
        data-testid="cruise-filter-footer-clear-all"
      >
        {intl.formatMessage({
          id: 'CLX1JYm50NiyJHejnrA2',
          defaultMessage: 'Reset all',
          description: 'Button label to reset all filters in the search or listing filters panel',
        })}
      </TextButton>
      <TextButton
        kind="primary"
        onClick={onApply}
        data-testid="cruise-filter-footer-apply"
      >
        {intl.formatMessage({
          id: 'zZNuM1A0SgjmEKYQ4Y1s',
          defaultMessage: 'Apply',
          description: 'Button label to apply the selected filter criteria in the search results panel',
        })}
      </TextButton>
    </Group>
  </Group>
}

export default CruiseFilterFooter
